/* eslint-disable @typescript-eslint/no-explicit-any */
import { useUploadFileMutation } from "@/services/file";
import { useCreateRequestMutation } from "@/services/request-access";
import { getUserFromLocalStorage } from "@/utils/getUserFromLocalStorage";
import { zodResolver } from "@hookform/resolvers/zod";
import { Load } from "components/load";
import { Button } from "components/ui/button";
import { toast } from "components/ui/use-toast";
import { ChevronLeft } from "lucide-react";
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { HistoricoTimeline, TimelineEvent } from "./components/timeline";
import { CardContract } from "@/pages/store-back-office/access-request/components/card-contract";
import { UserCard } from "./components/user-card";
import { FormData, FormSchema } from "./validate-form";
import { EnumTypeAccessRequest } from "@/enum/access_request";
import { getCurrentMMDDYYYYDate } from '@/utils/dates';
import { EmployeeSituation } from "@/constants/employee-situation";

export const description =
  "A product edit page. The product edit page has a form to edit the product details, stock, product category, product status, and product images. The product edit page has a sidebar navigation and a main content area. The main content area has a form to edit the product details, stock, product category, product status, and product images. The sidebar navigation has as to product details, stock, product category, product status, and product images.";

export const iframeHeight = "1100px";

export const containerClassName = "w-full h-full";

export const CreateRequest: React.FC = () => {
  const navigate = useNavigate();
  const formMethods = useForm<FormData>({
    resolver: zodResolver(FormSchema),
  });
  const [createRequest, { isLoading }] = useCreateRequestMutation();
  const [uploadFile, { isLoading: isLoadingUpload }] = useUploadFileMutation();
  const [canCreateRequest, setCanCreateRequest] = useState(false);

  const isCreating = isLoading || isLoadingUpload;
  const currentSituation = formMethods.watch('situation');

  const handleValidationChange = (isValid: boolean) => {
    setCanCreateRequest(isValid);
  };

  const handleCreateRequest = async () => {
    const values = formMethods.getValues();

    if (values?.situation === EmployeeSituation.DISMISSED) {
      toast({
        variant: "destructive",
        description: "Não é possível criar solicitação para funcionário demitido.",
        duration: 5000,
      });
      return;
    }

    let createdId;
    const allFiles = Object.keys(values)
      .filter((key) => key.includes("contract"))
      .map((key) => (values as any)[key])
      .filter((fileArr: any) => Array.isArray(fileArr) && fileArr.length > 0 && fileArr[0]);

    if (allFiles.length === 0) {
      toast({
        variant: "alert",
        description:
          "Anexe um contrato para continuar, contrato é obrigatório para a solicitação de acesso",
        duration: 5000,
      });
      const targetElement = document.getElementById("contract");
      if (targetElement) {
        targetElement.scrollIntoView({ behavior: "smooth" });
      }

      return;
    }

    if (!values?.name && !values?.cpf && !values?.store) {
      toast({
        variant: "alert",
        description: "Adicione um CPF e aguarde a busca do usuário",
        duration: 5000,
      });
      return;
    }
    try {
      createdId = await createRequest({
        name: values?.name as string,
        cpf: values?.cpf.toString(),
        store: values?.store?.toString() as string,
      }).unwrap();

      toast({
        variant: "default",
        description: "Requisição criada com sucesso!",
        duration: 5000,
      });
    } catch (error) {
      console.error("Erro ao criar requisição:", error);
      toast({
        variant: "error",
        description: "Erro ao criar a requisição",
        duration: 5000,
      });
      return;
    }

    if (allFiles && allFiles.length > 0) {
      for (const file of allFiles) {
        try {
          console.log(file[0]);
          console.log(file);
          await uploadFile({
            file: file[0],
            entity_name: "ACCESS_REQUEST",
            entity_id: createdId,
          }).unwrap();
          toast({
            variant: "default",
            description: "Arquivo enviado com sucesso!",
            duration: 5000,
          });
        } catch (error) {
          console.error("Erro ao enviar arquivo:", error);
          toast({
            variant: "destructive",
            description: `Ocorreu um erro ao enviar contrato ${(file[0] as any)?.name}`,
          });
        }
      }
    }

    formMethods.reset();

    navigate("/retaguarda-de-loja/requisicao-de-acesso/");
  };

  const mapTimelineEventData = (): TimelineEvent[] => {
    const user = getUserFromLocalStorage();
    const currentDate = getCurrentMMDDYYYYDate();
    return [{
      id: "1",
      created_at: currentDate,
      responsible: user?.name || "Desconhecido",
      details: "Requisição de acesso inicial: aguardando criação",
      type: EnumTypeAccessRequest.CREATED,
    }]
  };

  return (
    <>
      {isCreating && (
        <div className="w-full h-screen bg-white opacity-60 fixed top-0 left-0 z-50 pointer-events-auto">
          <Load />
        </div>
      )}

      <div className="flex flex-col min-h-screen w-full">
        <div className="flex min-h-screen w-full flex-col bg-muted/40">
          <div className="bg-white flex flex-col sm:gap-4 sm:py-4">
            <main className="grid flex-1 items-start gap-4 p-4 sm:px-6 sm:py-0 md:gap-8">
              <div className="mx-auto grid w-full flex-1 auto-rows-max gap-4">
                <div className="flex items-center gap-4">
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => navigate(-1)}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    <span className="sr-only">Voltar</span>
                  </Button>
                  <h1 className="flex-1 shrink-0 whitespace-nowrap text-xl font-semibold tracking-tight sm:grow-0">
                    Solicitação de Acesso
                  </h1>

                  <div className="hidden items-center gap-2 md:ml-auto md:flex">
                    <Button
                      size="sm"
                      onClick={() => handleCreateRequest()}
                      disabled={!canCreateRequest || currentSituation === EmployeeSituation.DISMISSED}
                    >
                      {isLoading ? "Solicitando..." : "Solicitar Acesso"}
                    </Button>
                  </div>
                </div>
                <div className="grid gap-4 md:grid-cols-[1fr_250px] lg:grid-cols-3 lg:gap-8">
                  <div className="grid auto-rows-max items-start gap-4 lg:col-span-2 lg:gap-8">
                    <UserCard formMethods={formMethods} onValidationChange={handleValidationChange} />
                    <CardContract formMethods={formMethods} />
                  </div>
                  <div className="grid auto-rows-max items-start gap-4 lg:gap-8">
                    <HistoricoTimeline event={mapTimelineEventData()} />
                  </div>
                </div>
                <div className="flex items-center justify-center gap-2 md:hidden">
                  <Button variant="outline" size="sm">
                    Descartar
                  </Button>
                  <Button 
                    size="sm" 
                    onClick={() => handleCreateRequest()}
                    disabled={!canCreateRequest || currentSituation === EmployeeSituation.DISMISSED}
                  >
                    Salvar
                  </Button>
                </div>
              </div>
            </main>
          </div>
        </div>
      </div>
    </>
  );
};